/**
 * Validation functions — each returns { ok: boolean, errors: string[] }.
 * No side effects, independently testable.
 */

/**
 * @param {{ apiKey?: string, authDomain?: string, databaseURL?: string, projectId?: string }} cfg
 */
export function validateFirebaseConfig(cfg) {
  const errors = [];
  if (!cfg?.apiKey)       errors.push('apiKey is required');
  if (!cfg?.authDomain)   errors.push('authDomain is required');
  if (!cfg?.databaseURL)  errors.push('databaseURL is required');
  if (!cfg?.projectId)    errors.push('projectId is required');
  return { ok: errors.length === 0, errors };
}

/**
 * @param {{ name: string, members: string[] }} setup
 * @param {{ minMembers?: number, maxMembers?: number }} opts
 */
export function validateTeamSetup(setup, { minMembers = 4, maxMembers = 9 } = {}) {
  const errors = [];
  if (!setup?.name?.trim()) errors.push('Team name is required');
  const filled = (setup?.members ?? []).filter(m => m?.trim());
  if (filled.length < minMembers) errors.push(`At least ${minMembers} member names are required`);
  if (filled.length > maxMembers) errors.push(`Maximum ${maxMembers} members allowed`);
  return { ok: errors.length === 0, errors };
}

/**
 * Checks whether a team submission is ready to be submitted.
 * @param {{
 *   fields: Array<{ id: string }>,
 *   steward: string[],
 *   bombFired: boolean,
 * }} round
 * @param {{
 *   fieldValues: Record<string, string>,
 *   stewardValues: string[],
 *   bombValue: string,
 *   signedCount: number,
 *   memberCount: number,
 * }} answers
 */
export function validateSubmission(round, answers) {
  const errors = [];
  const { fields, steward, bombFired } = round;
  const { fieldValues, stewardValues, bombValue, signedCount, memberCount } = answers;

  fields.forEach(f => {
    if (!fieldValues?.[f.id]?.trim()) errors.push(`"${f.label}" is required`);
  });

  steward.forEach((q, i) => {
    if (!stewardValues?.[i]?.trim()) errors.push(`Steward check ${i + 1} is required`);
  });

  if (!bombFired) {
    errors.push('Waiting for the bomb drop — cannot submit yet');
  } else if (!bombValue?.trim()) {
    errors.push('Bomb drop response is required');
  }

  const sigsNeeded = Math.min(4, memberCount);
  if (signedCount < sigsNeeded) {
    errors.push(`${signedCount} of ${sigsNeeded} required signatures collected`);
  }

  return { ok: errors.length === 0, errors };
}

/**
 * Validates a content pack before it is saved or imported. Deliberately
 * lenient — a pack is a *partial text overlay* onto the built-in defaults
 * (see content.js applyContentPack), so missing sections are fine; this
 * only rejects things that would corrupt the overlay: wrong container
 * types, or non-string values where text is expected.
 * @param {object} pack
 */
export function validateContentPack(pack) {
  const errors = [];
  if (!pack || typeof pack !== 'object' || Array.isArray(pack)) {
    return { ok: false, errors: ['Pack must be a JSON object'] };
  }
  if (pack.brand != null && (typeof pack.brand !== 'object' || Array.isArray(pack.brand))) {
    errors.push('brand must be an object');
  }
  if (pack.privacy != null && typeof pack.privacy !== 'string') {
    errors.push('privacy must be a string');
  }
  if (pack.badges != null) {
    if (!Array.isArray(pack.badges)) errors.push('badges must be an array');
    else pack.badges.forEach((b, i) => {
      if (b != null && typeof b !== 'object') errors.push(`badges[${i}] must be an object`);
    });
  }
  if (pack.rounds != null) {
    if (!Array.isArray(pack.rounds)) errors.push('rounds must be an array');
    else pack.rounds.forEach((r, i) => {
      if (r == null) return;
      if (typeof r !== 'object' || Array.isArray(r)) { errors.push(`rounds[${i}] must be an object`); return; }
      for (const k of ['name', 'eyebrow', 'sit', 'brief', 'bomb', 'bombDim']) {
        if (r[k] != null && typeof r[k] !== 'string') errors.push(`rounds[${i}].${k} must be a string`);
      }
      if (r.dur != null && !Number.isFinite(r.dur)) errors.push(`rounds[${i}].dur must be a number`);
      for (const k of ['fields', 'steward', 'dims', 'priorities', 'options']) {
        if (r[k] != null && !Array.isArray(r[k])) errors.push(`rounds[${i}].${k} must be an array`);
      }
    });
  }
  return { ok: errors.length === 0, errors };
}

/**
 * Validates that a round config object has all required fields.
 * Used in content.test.js to guard against misconfigured simulations.
 * @param {object} round
 */
export function validateRoundShape(round) {
  const required = ['name','eyebrow','dur','max','sit','brief','bomb','bombField','fields','steward','dims','bombDim'];
  const errors = required.filter(k => round?.[k] == null).map(k => `Missing: ${k}`);
  if (!Array.isArray(round?.fields) || round.fields.length === 0)
    errors.push('fields must be a non-empty array');
  if (!Array.isArray(round?.dims) || round.dims.length === 0)
    errors.push('dims must be a non-empty array');
  if (!Array.isArray(round?.steward) || round.steward.length === 0)
    errors.push('steward must be a non-empty array');
  return { ok: errors.length === 0, errors };
}
